import { useEffect, useState } from 'react';
import { api } from './api';

// The tenant's plan entitlements — which modules/features are unlocked.
// Fetched once from GET /auth/me/entitlements and cached in-module so the
// Sidebar, route guards, and the 403 handler all agree without refetching.
export interface Entitlements {
  state: string;
  features: string[];
  modules: string[];
  super_admin?: boolean;
}

// Which module each app route/nav belongs to. Anything not listed here is
// ungated (dashboard, settings, profile, etc.).
export const MODULE_LABELS: Record<string, string> = {
  mod_document_processing: 'Document Processing',
  mod_tasks_compliance: 'Tasks & Compliance',
  mod_real_estate: 'Real Estate',
  mod_insurance_licenses: 'Insurance & Licenses',
};

let cache: Entitlements | null = null;
let inflight: Promise<Entitlements> | null = null;
const listeners = new Set<(e: Entitlements) => void>();

export function getCachedEntitlements(): Entitlements | null {
  return cache;
}

export async function fetchEntitlements(force = false): Promise<Entitlements> {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = api
    .get('/auth/me/entitlements')
    .then((res) => {
      cache = res.data as Entitlements;
      lastFetchedAt = Date.now();
      listeners.forEach((l) => l(cache!));
      return cache;
    })
    .catch(() => {
      // Fail open on error so a transient hiccup never hides the whole app —
      // the backend gate is the real enforcement; nav visibility is cosmetic.
      // Crucially, do NOT cache this fallback: an empty-modules object is truthy
      // and would make hasModule() fail *closed* (hiding every gated nav item)
      // for the rest of the SPA session. Leaving cache null keeps hasModule()
      // fail-open (null => show) and lets the next call refetch — e.g. the
      // unauthenticated 401 that fires before login must not poison the cache.
      cache = null;
      const fallback: Entitlements = { state: 'unknown', features: [], modules: [], super_admin: false };
      return fallback;
    })
    .finally(() => { inflight = null; });
  return inflight;
}

export function clearEntitlements() {
  cache = null;
  inflight = null;
  lastFetchedAt = 0;
}

// ── Keeping up with operator changes ────────────────────────────────────────
// Entitlements were fetched exactly once per SPA session. When an operator
// granted or revoked a module in the Site Panel, the API reflected it on the
// next call but the browser kept the stale module list — so the nav still hid a
// module the tenant now owned (or still offered one they had lost) until a full
// page reload. Subscription STATE changes still bit immediately, because the
// server-side guard answers 402 per request; only this client cache lagged.
//
// Refetch when the tab regains focus rather than polling: an operator change is
// followed by the user coming back to the app, and an idle background tab costs
// nothing. Rate-limited so alt-tabbing doesn't hammer the endpoint.
let lastFetchedAt = 0;
const REFRESH_AFTER_MS = 60_000;

function refreshIfStale() {
  // Only when something is already loaded — no cache means either logged out or
  // a failed load, and both are handled by the normal fetch path.
  if (!cache) return;
  if (Date.now() - lastFetchedAt < REFRESH_AFTER_MS) return;
  fetchEntitlements(true).catch(() => { /* fail open, as above */ });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.addEventListener('focus', refreshIfStale);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshIfStale();
  });
}

// Fail open: until entitlements load (cache null), show everything so nothing
// flickers or disappears for grandfathered/super users. Once loaded, hide
// modules not in the plan. super_admin always sees everything.
export function hasModule(code: string, e: Entitlements | null = cache): boolean {
  if (!e) return true;
  if (e.super_admin) return true;
  return e.modules.includes(code) || e.features.includes(code);
}

export function useEntitlements() {
  const [ent, setEnt] = useState<Entitlements | null>(cache);
  useEffect(() => {
    let active = true;
    const onUpdate = (e: Entitlements) => { if (active) setEnt(e); };
    listeners.add(onUpdate);
    fetchEntitlements().then((e) => { if (active) setEnt(e); });
    return () => { active = false; listeners.delete(onUpdate); };
  }, []);
  return {
    ent,
    hasModule: (code: string) => hasModule(code, ent),
    loading: ent === null,
  };
}
